import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FlightTicket, FlightStatus } from '../models';
import { toWallClockIso, parseWallClockIso } from '../utils/wallClockTime';

export class FlightService {
    private static getCollectionPath(tripId: string) {
        return `trips/${tripId}/flightTickets`;
    }

    /**
     * Parse a stored flight time into a Date. Flight times are now stored as
     * timezone-agnostic wall-clock ISO strings (see wallClockTime.ts), but
     * legacy docs may still hold Firestore Timestamps — handle both. An
     * offset-less string is parsed as local wall-clock, which preserves the
     * original airport time regardless of the viewer's timezone. A string that
     * still carries a `Z`/offset (an older doc, or an AI parse that added one)
     * has it dropped rather than converted — see parseWallClockIso.
     */
    private static parseFlightTime(value: any): Date | null | undefined {
        if (value == null) return value;
        if (value instanceof Timestamp) return value.toDate();
        if (value instanceof Date) return value;
        if (typeof value === 'string') return parseWallClockIso(value);
        return value;
    }

    private static convertTimestamps(data: any): any {
        const converted = { ...data };
        if ('departureTime' in data) {
            converted.departureTime = this.parseFlightTime(data.departureTime);
        }
        if ('arrivalTime' in data) {
            converted.arrivalTime = this.parseFlightTime(data.arrivalTime);
        }
        if (data.createdAt instanceof Timestamp) {
            converted.createdAt = data.createdAt.toDate();
        }
        if (data.updatedAt instanceof Timestamp) {
            converted.updatedAt = data.updatedAt.toDate();
        }
        return converted;
    }

    // Get all flights for a trip
    static async getFlights(tripId: string): Promise<FlightTicket[]> {
        try {
            console.log('🔍 [FlightService] Getting flights for trip:', tripId);
            const flightsRef = collection(db, this.getCollectionPath(tripId));
            const snapshot = await getDocs(flightsRef);

            console.log('✅ [FlightService] Found', snapshot.size, 'flights');
            const flights = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    tripId,
                    ...this.convertTimestamps(data),
                } as FlightTicket;
            });

            // Sort by departure time
            flights.sort((a, b) => {
                const dateA = a.departureTime ? new Date(a.departureTime).getTime() : 0;
                const dateB = b.departureTime ? new Date(b.departureTime).getTime() : 0;
                return dateA - dateB;
            });

            return flights;
        } catch (error) {
            console.error('❌ [FlightService] Error getting flights:', error);
            throw error;
        }
    }

    // Subscribe to flights
    static subscribeToFlights(
        tripId: string,
        callback: (flights: FlightTicket[]) => void
    ): () => void {
        console.log('🔍 [FlightService] Setting up flight subscription for trip:', tripId);
        const flightsRef = collection(db, this.getCollectionPath(tripId));

        return onSnapshot(
            flightsRef,
            (snapshot) => {
                console.log('📡 [FlightService] Flight update:', snapshot.size, 'flights');
                const flights = snapshot.docs.map((doc) => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        tripId,
                        ...this.convertTimestamps(data),
                    } as FlightTicket;
                });

                // Sort by departure time
                flights.sort((a, b) => {
                    const dateA = a.departureTime ? new Date(a.departureTime).getTime() : 0;
                    const dateB = b.departureTime ? new Date(b.departureTime).getTime() : 0;
                    return dateA - dateB;
                });

                callback(flights);
            },
            (error) => {
                console.error('❌ [FlightService] Subscription error:', error);
            }
        );
    }

    // Add new flight
    static async addFlight(
        tripId: string,
        flightData: Omit<FlightTicket, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
    ): Promise<string> {
        try {
            console.log('➕ [FlightService] Adding flight:', flightData.title);
            const flightsRef = collection(db, this.getCollectionPath(tripId));

            const now = new Date();
            const rawData: Record<string, any> = {
                ...flightData,
                tripId,
                departureTime: flightData.departureTime ? toWallClockIso(flightData.departureTime) : null,
                arrivalTime: flightData.arrivalTime ? toWallClockIso(flightData.arrivalTime) : null,
                createdAt: Timestamp.fromDate(now),
                updatedAt: Timestamp.fromDate(now),
            };
            // Strip undefined values — Firestore rejects them
            const cleanData = Object.fromEntries(
                Object.entries(rawData).filter(([, v]) => v !== undefined)
            );
            const docRef = await addDoc(flightsRef, cleanData);

            console.log('✅ [FlightService] Flight added with ID:', docRef.id);
            return docRef.id;
        } catch (error) {
            console.error('❌ [FlightService] Error adding flight:', error);
            throw error;
        }
    }

    // Update flight
    static async updateFlight(
        tripId: string,
        flightId: string,
        flightData: Partial<Omit<FlightTicket, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>>
    ): Promise<void> {
        try {
            console.log('✏️ [FlightService] Updating flight:', flightId);
            const flightRef = doc(db, this.getCollectionPath(tripId), flightId);

            const rawData: Record<string, any> = {
                ...flightData,
                updatedAt: Timestamp.fromDate(new Date()),
            };

            // Convert dates: use Timestamp when present, null when explicitly
            // provided but empty (so the field can be cleared).
            if ('departureTime' in flightData) {
                rawData.departureTime = flightData.departureTime
                    ? toWallClockIso(flightData.departureTime)
                    : null;
            }
            if ('arrivalTime' in flightData) {
                rawData.arrivalTime = flightData.arrivalTime
                    ? toWallClockIso(flightData.arrivalTime)
                    : null;
            }

            // Strip undefined values — Firestore rejects them
            const updateData = Object.fromEntries(
                Object.entries(rawData).filter(([, v]) => v !== undefined)
            );

            await updateDoc(flightRef, updateData);
            console.log('✅ [FlightService] Flight updated');
        } catch (error) {
            console.error('❌ [FlightService] Error updating flight:', error);
            throw error;
        }
    }

    // Delete flight
    static async deleteFlight(tripId: string, flightId: string): Promise<void> {
        try {
            console.log('🗑️ [FlightService] Deleting flight:', flightId);
            const flightRef = doc(db, this.getCollectionPath(tripId), flightId);
            await deleteDoc(flightRef);
            console.log('✅ [FlightService] Flight deleted');
        } catch (error) {
            console.error('❌ [FlightService] Error deleting flight:', error);
            throw error;
        }
    }
}
